import { useState, useRef, DragEvent, useEffect, useMemo } from 'react';
import {
  FaPaperPlane,
  FaPaperclip,
  FaTimes,
  FaFile,
  FaClock,
  FaLock,
  FaLockOpen,
  FaMicrophone,
  FaStop,
  FaSpinner,
} from 'react-icons/fa';
import api from '@/services/api';
import socketService from '@/services/socket';
import * as encryption from '@/services/encryption';
import { createTimestampMarker } from '@/utils/dateTime';
import { useToast } from '@/components/common/ToastProvider';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import MentionAutocomplete, { MentionItem } from './MentionAutocomplete';

interface MessageInputProps {
  channelId: string;
  recipientPublicKey?: string;
}

interface FileUpload {
  file: File;
  preview?: string;
  progress: number;
  error?: string;
  isEncrypted?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'video/mp4',
  'audio/mpeg',
  'audio/wav',
];

export default function MessageInput({ channelId, recipientPublicKey }: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [serverLimits, setServerLimits] = useState<any>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const recognitionRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const isTyping = useRef(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isEncryptionEnabled = encryption.isEncryptionEnabled() && recipientPublicKey;
  const toast = useToast();

  // Mention autocomplete state
  const [channelMembers, setChannelMembers] = useState<MentionItem[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  // Fetch channel members for mention autocomplete
  useEffect(() => {
    let isMounted = true;
    const fetchMembers = async () => {
      try {
        const res = await api.get(`/channels/${channelId}/online-users`);
        if (isMounted) {
          const users: MentionItem[] = (res.data.users || []).map((u: any) => ({
            id: u.id,
            username: u.username,
            avatarUrl: u.avatarUrl,
            status: u.status,
          }));
          setChannelMembers(users);
        }
      } catch (e) {
        console.warn('Failed to fetch members for mentions:', e);
      }
    };
    fetchMembers();
    return () => {
      isMounted = false;
    };
  }, [channelId]);

  const filteredMentions = useMemo(() => {
    if (mentionQuery === null) return [];

    const specials: MentionItem[] = [
      {
        id: 'special-everyone',
        username: 'everyone',
        isSpecial: true,
        description: 'Notify everyone in this channel',
      },
      {
        id: 'special-here',
        username: 'here',
        isSpecial: true,
        description: 'Notify active members',
      },
    ];

    const matchedSpecials = specials.filter((s) =>
      s.username.toLowerCase().startsWith(mentionQuery),
    );

    const matchedUsers = channelMembers.filter((u) =>
      u.username.toLowerCase().includes(mentionQuery),
    );

    return [...matchedSpecials, ...matchedUsers];
  }, [mentionQuery, channelMembers]);

  const insertMention = (username: string) => {
    const input = messageInputRef.current;
    const cursor = input?.selectionStart ?? message.length;
    const textBeforeCursor = message.slice(0, cursor);
    const textAfterCursor = message.slice(cursor);
    const newBefore = textBeforeCursor.replace(/@([a-zA-Z0-9_-]*)$/, `@${username} `);
    const newText = newBefore + textAfterCursor;
    setMessage(newText);
    setMentionQuery(null);

    setTimeout(() => {
      if (input) {
        input.focus();
        const newPos = newBefore.length;
        input.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMessage(val);
    handleTyping();

    const cursor = e.target.selectionStart || val.length;
    const textBeforeCursor = val.slice(0, cursor);
    const mentionMatch = textBeforeCursor.match(/@([a-zA-Z0-9_-]*)$/);

    if (mentionMatch) {
      setMentionQuery(mentionMatch[1].toLowerCase());
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  // Auto-focus when opening or switching channels
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const res = await api.get('/uploads/limits');
        setServerLimits(res.data);
      } catch (err) {
        console.error('Failed to fetch server limits', err);
      }
    };
    fetchLimits();
  }, []);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);

  useEffect(() => {
    // Defer to end of render to avoid race conditions
    const id = setTimeout(() => {
      const el = messageInputRef.current;
      if (el) {
        el.focus();
        try {
          const len = el.value.length;
          el.setSelectionRange(len, len);
        } catch {}
      }
    }, 0);
    return () => clearTimeout(id);
  }, [channelId]);

  // Cleanup typing indicator when component unmounts or channel changes
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (isTyping.current) {
        socketService.stopTyping(channelId);
        isTyping.current = false;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [channelId]);

  const insertTimestamp = (timestamp: number) => {
    const marker = createTimestampMarker(timestamp, 'f');
    setMessage((prev) => `${prev.trim()} ${marker}`.trim());
  };

  // Typing indicator with debouncing (3s auto-clear)
  const handleTyping = () => {
    window.dispatchEvent(new Event('user-activity'));
    if (!isTyping.current) {
      socketService.startTyping(channelId);
      isTyping.current = true;
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping.current) {
        socketService.stopTyping(channelId);
        isTyping.current = false;
      }
    }, 3000);
  };
  const sendMessage = async () => {
    if ((!message.trim() && uploads.length === 0) || sending) return;

    window.dispatchEvent(new Event('user-activity'));

    // Check for upload errors
    const hasErrors = uploads.some((u) => u.error);
    if (hasErrors) {
      toast.error('Please remove files with errors before sending');
      return;
    }

    // Stop typing indicator when sending
    if (isTyping.current) {
      socketService.stopTyping(channelId);
      isTyping.current = false;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    setSending(true);

    try {
      let contentToSend = message.trim();
      let encrypted = false;
      // If only attachments are being sent, use a placeholder so validation passes
      if (!contentToSend && uploads.length > 0) {
        contentToSend = '[attachment]';
      }

      // Encrypt message if enabled
      if (isEncryptionEnabled && contentToSend) {
        const secretKey = encryption.getSecretKey();
        if (secretKey && recipientPublicKey) {
          const encryptedContent = encryption.encryptMessage(
            contentToSend,
            recipientPublicKey,
            secretKey,
          );
          if (encryptedContent) {
            contentToSend = encryptedContent;
            encrypted = true;
          } else {
            toast.error('Failed to encrypt message');
            return;
          }
        }
      }

      // First create the message to get its id
      const response = await api.post(`/messages/channels/${channelId}`, {
        content: contentToSend || ' ', // Send space if only files
        encrypted,
      });

      const createdMessage = response.data.data;

      // Upload files with the created messageId
      const attachments: any[] = [];
      for (const upload of uploads) {
        const attachment = await uploadFile(
          upload.file,
          createdMessage.id,
          upload.isEncrypted && serverLimits?.enableFileEncryption,
        );
        if (attachment) {
          // Backend returns FileAttachment with correct field names
          attachments.push(attachment);
        }
      }

      // Emit message to local socket with attachments for immediate UI
      console.log('[MessageInput] Sending message via socket:', {
        messageId: createdMessage.id,
        channelId,
      });
      socketService.emit('message:send', {
        ...createdMessage,
        channelId,
        content: message.trim() || '[attachment]',
        encrypted,
        attachments,
      });

      setMessage('');
      setUploads([]);
      // Revoke preview URLs
      uploads.forEach((upload) => {
        if (upload.preview) URL.revokeObjectURL(upload.preview);
      });
    } catch (error: any) {
      console.error('Error sending message:', error);
      const errorMessage =
        error.response?.data?.error || 'Failed to send message. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSending(false);
      // Re-focus after any sending state changes
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
          const len = messageInputRef.current.value.length;
          try {
            messageInputRef.current.setSelectionRange(len, len);
          } catch {}
        }
      }, 0);
    }
  };

  const toggleDictation = () => {
    if (isDictating) {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
      setIsDictating(false);
    } else {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        let currentDictation = message;
        let interimDictation = '';

        recognition.onresult = (event: any) => {
          interimDictation = '';
          let finalDictation = currentDictation;
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
              finalDictation += (finalDictation ? ' ' : '') + event.results[i][0].transcript;
              currentDictation = finalDictation;
            } else {
              interimDictation += event.results[i][0].transcript;
            }
          }
          setMessage(finalDictation + (interimDictation ? ' ' + interimDictation : ''));
        };

        recognition.onerror = (event: any) => {
          console.warn('Dictation error:', event.error);
          setIsDictating(false);
        };

        recognition.onend = () => {
          setIsDictating(false);
        };

        try {
          recognition.start();
          recognitionRef.current = recognition;
          setIsDictating(true);
        } catch (e) {
          console.error('Failed to start dictation:', e);
        }
      } else {
        alert('Speech recognition is not supported in your browser.');
      }
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMessage();
  };

  const uploadFile = async (
    file: File,
    messageId?: string,
    isEncrypted?: boolean,
  ): Promise<any | null> => {
    try {
      let finalFile = file;
      if (isEncrypted && recipientPublicKey) {
        const secretKey = encryption.getSecretKey();
        if (secretKey) {
          const buffer = await file.arrayBuffer();
          const encryptedBlob = encryption.encryptFile(buffer, recipientPublicKey, secretKey);
          if (encryptedBlob) {
            finalFile = new File([encryptedBlob], file.name, { type: file.type });
          } else {
            toast.error('Failed to encrypt file ' + file.name);
            return null;
          }
        }
      }

      const formData = new FormData();
      formData.append('file', finalFile);
      if (messageId) {
        formData.append('messageId', messageId);
      }
      if (isEncrypted) {
        formData.append('isEncrypted', 'true');
      }

      // Delete the default Content-Type so axios sets multipart boundary
      const response = await api.post('/uploads/upload', formData, {
        headers: {
          'Content-Type': undefined,
        },
      });

      // When messageId is provided, backend returns { attachment }
      // Otherwise returns { file }
      return response.data.attachment || response.data.file || response.data.data;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      const errorMessage = error.response?.data?.error || 'Failed to upload file';
      toast.error(errorMessage);
      return null;
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const addFiles = (files: File[]) => {
    const newUploads: FileUpload[] = [];

    files.forEach((file) => {
      let error: string | undefined;
      let preview: string | undefined;

      if (file.size > MAX_FILE_SIZE) {
        error = 'File too large (max 10MB)';
      } else if (!ALLOWED_TYPES.includes(file.type)) {
        error = 'File type not allowed';
      }

      // Create preview for images
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      newUploads.push({
        file,
        preview,
        progress: 0,
        error,
        isEncrypted: false,
      });
    });

    setUploads((prev) => [...prev, ...newUploads]);
  };

  const toggleEncryption = (index: number) => {
    const upload = uploads[index];
    if (upload.isEncrypted) {
      setUploads((prev) => prev.map((u, i) => (i === index ? { ...u, isEncrypted: false } : u)));
    } else {
      if (
        window.confirm(
          'Encrypting large files may be slow. Are you sure you want to encrypt this file?',
        )
      ) {
        setUploads((prev) => prev.map((u, i) => (i === index ? { ...u, isEncrypted: true } : u)));
      }
    }
  };

  const removeFile = (index: number) => {
    const upload = uploads[index];
    if (upload.preview) {
      URL.revokeObjectURL(upload.preview);
    }
    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  return (
    <div
      className={`border-t border-brand-outline-variant dark:border-brand-outline glass-effect rounded-t-xl ${
        isDragging
          ? 'bg-primary-50 dark:bg-primary-900 border-primary-500 border-dashed border-2'
          : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {uploads.length > 0 && (
        <div className="bg-transparent border-b border-brand-outline-variant dark:border-brand-outline p-4">
          <div className="text-xs font-medium text-brand-on-surface-variant dark:text-brand-on-surface-variant mb-2">
            Attachments ({uploads.length})
          </div>
          <div className="flex flex-wrap gap-3">
            {uploads.map((upload, index) => (
              <div
                key={index}
                className={`relative group ${
                  upload.error
                    ? 'border-2 border-red-300 dark:border-red-700'
                    : 'border border-brand-outline-variant dark:border-brand-outline-variant'
                } rounded-lg overflow-hidden bg-brand-surface/70 dark:bg-brand-background/50 backdrop-blur-md`}
                style={{ width: upload.preview ? '120px' : 'auto' }}
              >
                {upload.preview ? (
                  // Image preview
                  <div className="relative">
                    <img
                      src={upload.preview}
                      alt={upload.file.name}
                      className="w-full h-24 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-brand-on-primary text-xs px-1 py-0.5 truncate">
                      {upload.file.name}
                    </div>
                    {isEncryptionEnabled && serverLimits?.enableFileEncryption && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleEncryption(index);
                        }}
                        className="absolute top-1 left-1 bg-brand-background bg-opacity-70 text-brand-on-primary rounded-full p-1 transition-opacity focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none focus-visible:opacity-100"
                        title={
                          upload.isEncrypted
                            ? 'File encrypted. Click to disable.'
                            : 'File unencrypted. Click to encrypt.'
                        }
                        aria-label={
                          upload.isEncrypted ? 'Disable file encryption' : 'Enable file encryption'
                        }
                        aria-pressed={upload.isEncrypted}
                      >
                        {upload.isEncrypted ? (
                          <FaLock className="text-green-500" size={12} />
                        ) : (
                          <FaLockOpen className="text-brand-error" size={12} />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="absolute top-1 right-1 bg-brand-error hover:bg-brand-error text-brand-on-primary rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none focus-visible:opacity-100"
                      aria-label="Remove file"
                      title="Remove file"
                    >
                      <FaTimes size={10} />
                    </button>
                  </div>
                ) : (
                  // File preview
                  <div className="flex items-center gap-2 px-3 py-2 min-w-[200px]">
                    <FaFile className="text-brand-on-surface-variant flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{upload.file.name}</div>
                      <div className="text-xs text-brand-outline">
                        {formatFileSize(upload.file.size)}
                      </div>
                    </div>
                    {isEncryptionEnabled && serverLimits?.enableFileEncryption && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleEncryption(index);
                        }}
                        className="text-brand-on-surface-variant flex-shrink-0 focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none rounded-full"
                        title={
                          upload.isEncrypted
                            ? 'File encrypted. Click to disable.'
                            : 'File unencrypted. Click to encrypt.'
                        }
                        aria-label={
                          upload.isEncrypted ? 'Disable file encryption' : 'Enable file encryption'
                        }
                        aria-pressed={upload.isEncrypted}
                      >
                        {upload.isEncrypted ? (
                          <FaLock className="text-green-500" size={14} />
                        ) : (
                          <FaLockOpen className="text-brand-error" size={14} />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-brand-on-surface-variant hover:text-brand-error flex-shrink-0 focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none rounded-full"
                      aria-label="Remove file"
                      title="Remove file"
                    >
                      <FaTimes size={14} />
                    </button>
                  </div>
                )}
                {upload.error && (
                  <div className="absolute inset-0 bg-brand-error bg-opacity-90 flex items-center justify-center">
                    <div className="text-brand-on-primary text-xs font-medium px-2 text-center">
                      {upload.error}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSend} className="p-3 sm:p-4">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2.5 sm:p-2 rounded-full hover:bg-white/10 dark:hover:bg-white/10 text-brand-outline hover:text-white active:scale-95 transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer"
            title="Attach file"
            aria-label="Attach file"
          >
            <FaPaperclip className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`p-2.5 sm:p-2 rounded-full active:scale-95 transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer ${
                showDatePicker
                  ? 'bg-primary-600/30 text-primary-400'
                  : 'hover:bg-white/10 dark:hover:bg-white/10 text-brand-outline hover:text-white'
              }`}
              title="Insert date/time"
              aria-label="Insert date/time"
              aria-expanded={showDatePicker}
              aria-haspopup="dialog"
            >
              <FaClock className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <button
              type="button"
              onClick={toggleDictation}
              className={`p-2.5 sm:p-2 rounded-full active:scale-95 transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer ${
                isDictating
                  ? 'bg-red-500/30 text-red-400 animate-pulse'
                  : 'hover:bg-white/10 dark:hover:bg-white/10 text-brand-outline hover:text-white'
              }`}
              title={isDictating ? 'Stop dictating' : 'Dictate message'}
              aria-label={isDictating ? 'Stop dictating' : 'Dictate message'}
              aria-pressed={isDictating}
            >
              {isDictating ? (
                <FaStop className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <FaMicrophone className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
            {showDatePicker && (
              <DateTimePicker onSelect={insertTimestamp} onClose={() => setShowDatePicker(false)} />
            )}
          </div>
          <div className="relative flex-1">
            {/* Mention autocomplete dropdown */}
            <MentionAutocomplete
              isOpen={mentionQuery !== null}
              query={mentionQuery || ''}
              members={filteredMentions}
              selectedIndex={mentionIndex}
              onSelect={insertMention}
              onClose={() => setMentionQuery(null)}
            />
            <input
              ref={messageInputRef}
              type="text"
              value={message}
              onChange={handleMessageChange}
              onKeyDown={async (e) => {
                if (mentionQuery !== null && filteredMentions.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setMentionIndex((prev) => (prev + 1) % filteredMentions.length);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setMentionIndex(
                      (prev) => (prev - 1 + filteredMentions.length) % filteredMentions.length,
                    );
                    return;
                  }
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    insertMention(filteredMentions[mentionIndex].username);
                    return;
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setMentionQuery(null);
                    return;
                  }
                }

                if (e.key === 'Enter') {
                  e.preventDefault();
                  await sendMessage();
                }
              }}
              placeholder="Type a message... (use @ to mention)"
              aria-label="Message content"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base bg-brand-background dark:bg-brand-surface-variant rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={(!message.trim() && uploads.length === 0) || sending}
            aria-label={sending ? 'Sending message' : 'Send message'}
            className="p-2.5 sm:p-2 rounded-full bg-primary-600 text-white hover:bg-primary-500 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer shadow-md hover:shadow-primary-500/25"
          >
            {sending ? (
              <FaSpinner className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" aria-hidden="true" />
            ) : (
              <FaPaperPlane className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
